import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  RotateCcw, Search, FileX, AlertTriangle, FileOutput, Loader2,
} from 'lucide-react';
import { pdvService } from '@/features/pdv/services/pdvService';
import { useAuth } from '@/hooks/useAuth';
import type { EstornoPendencia, EstornoResumoItem } from '@/features/pdv/types/pdv.types';

const fmt = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const fmtData = (s?: string) =>
  s ? new Date(s).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '—';

const hoje = () => new Date().toISOString().split('T')[0];
const ha30dias = () => {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return d.toISOString().split('T')[0];
};

// ════════════════════════════════════════════════════════════════════════════
//  HistoricoEstornos — lista vendas estornadas com filtros de período +
//  pendências fiscais de estorno (PDV-07: NFC-e que seguiu autorizada na
//  SEFAZ após o estorno → resolvíveis por NF-e de devolução).
// ════════════════════════════════════════════════════════════════════════════

export default function HistoricoEstornos() {
  const { user } = useAuth();
  const empresaId = user?.empresa.id ?? 0;
  const [de,  setDe]  = useState(ha30dias());
  const [ate, setAte] = useState(hoje());
  const [filtroAtivo, setFiltroAtivo] = useState({ de: ha30dias(), ate: hoje() });

  const { data: estornos, isLoading, isError } = useQuery<EstornoResumoItem[]>({
    queryKey: ['estornos', empresaId, filtroAtivo.de, filtroAtivo.ate],
    queryFn:  () => pdvService.listarEstornos(empresaId, filtroAtivo.de, filtroAtivo.ate),
    enabled:  empresaId > 0,
  });

  const aplicarFiltro = () => setFiltroAtivo({ de, ate });

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-3">
        <RotateCcw className="h-6 w-6 text-destructive" />
        <h1 className="text-2xl font-bold">Histórico de Estornos</h1>
      </div>

      {/* ── Pendências fiscais (PDV-07) ── */}
      <PendenciasFiscais />

      {/* ── Filtros ── */}
      <Card>
        <CardContent className="flex flex-wrap items-end gap-3 py-4">
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">De</label>
            <Input
              type="date"
              value={de}
              onChange={(e) => setDe(e.target.value)}
              className="w-40"
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Até</label>
            <Input
              type="date"
              value={ate}
              onChange={(e) => setAte(e.target.value)}
              className="w-40"
            />
          </div>
          <Button onClick={aplicarFiltro} disabled={isLoading}>
            <Search className="mr-2 h-4 w-4" />
            Filtrar
          </Button>
        </CardContent>
      </Card>

      {/* ── Resultados ── */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold text-muted-foreground">
            {isLoading
              ? 'Carregando...'
              : isError
              ? 'Erro ao carregar estornos.'
              : `${estornos?.length ?? 0} venda(s) estornada(s) no período`}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isError && (
            <div className="flex items-center gap-2 px-6 py-4 text-sm text-destructive">
              <AlertTriangle className="h-4 w-4" />
              Falha ao buscar estornos. Verifique a conexão com a API.
            </div>
          )}

          {!isLoading && !isError && (estornos?.length ?? 0) === 0 && (
            <div className="flex flex-col items-center gap-2 py-12 text-muted-foreground">
              <FileX className="h-10 w-10" />
              <p className="text-sm">Nenhum estorno encontrado no período selecionado.</p>
            </div>
          )}

          {(estornos?.length ?? 0) > 0 && (
            <div className="divide-y">
              {estornos!.map((item) => (
                <EstornoRow key={item.vendaId} item={item} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ── Pendências fiscais de estorno (PDV-07) ───────────────────────────────

function PendenciasFiscais() {
  const queryClient = useQueryClient();

  const { data: pendencias } = useQuery<EstornoPendencia[]>({
    queryKey: ['estornos-pendencias'],
    queryFn:  () => pdvService.listarPendenciasEstorno(),
  });

  const mutDevolucao = useMutation({
    mutationFn: (vendaId: number) => pdvService.emitirDevolucao(vendaId),
    onSuccess: (r) => {
      toast.success(
        r.reaproveitada
          ? `Devolução já autorizada (NF-e #${r.numeroNota}) — pendência resolvida.`
          : `NF-e de devolução #${r.numeroNota} autorizada — pendência resolvida.`,
      );
      queryClient.invalidateQueries({ queryKey: ['estornos-pendencias'] });
    },
    onError: (e: unknown) => {
      const msg =
        (e as { response?: { data?: { mensagem?: string } } })?.response?.data?.mensagem ??
        (e as Error).message;
      toast.error(msg);
    },
  });

  if (!pendencias?.length) return null;

  return (
    <Card className="border-destructive/40">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-sm font-semibold text-destructive">
          <AlertTriangle className="h-4 w-4" />
          {pendencias.length} pendência(s) fiscal(is) de estorno — NFC-e ainda ativa na SEFAZ
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="divide-y">
          {pendencias.map((p) => (
            <div
              key={p.logId}
              className="flex flex-col gap-2 px-6 py-4 sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="space-y-0.5">
                <div className="flex items-center gap-2">
                  <span className="font-semibold">Venda #{p.pdvVendaId}</span>
                  {p.numeroNota > 0 && (
                    <Badge variant="secondary" className="text-xs">NFC-e #{p.numeroNota}</Badge>
                  )}
                  <span className="text-sm font-medium">{fmt(p.totalNota)}</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  {fmtData(p.dtErro)} · {p.erro}
                </p>
                {p.chaveNota && (
                  <p className="font-mono text-[10px] text-muted-foreground">{p.chaveNota}</p>
                )}
              </div>
              {p.notaAutorizada ? (
                <Button
                  size="sm"
                  variant="destructive"
                  disabled={mutDevolucao.isPending}
                  onClick={() => mutDevolucao.mutate(p.pdvVendaId)}
                >
                  {mutDevolucao.isPending && mutDevolucao.variables === p.pdvVendaId ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <FileOutput className="mr-2 h-4 w-4" />
                  )}
                  Emitir devolução
                </Button>
              ) : (
                <Badge variant="outline" className="text-xs text-muted-foreground">
                  Nota não autorizada — tratar manualmente
                </Badge>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// ── Linha de estorno ──────────────────────────────────────────────────────

const ETAPA_LABEL: Record<string, string> = {
  estoque: 'Estoque',
  caixa: 'Caixa',
  nfce: 'NFC-e',
};

function EstornoRow({ item }: { item: EstornoResumoItem }) {
  const etapasComErro = item.etapasComErro ?? [];
  return (
    <div className="flex flex-col gap-1 px-6 py-4 hover:bg-muted/30 sm:flex-row sm:items-center sm:justify-between">
      <div className="space-y-0.5">
        <div className="flex items-center gap-2">
          <span className="font-semibold">Venda #{item.vendaId}</span>
          <NfceBadge notaEletronicaId={item.notaEletronicaId} />
          {etapasComErro.length > 0 && (
            <Badge variant="destructive" className="text-xs gap-1">
              <AlertTriangle className="h-3 w-3" />
              Estorno incompleto: {etapasComErro.map((e) => ETAPA_LABEL[e] ?? e).join(', ')}
            </Badge>
          )}
        </div>
        <p className="text-xs text-muted-foreground">
          Operador #{item.operadorId} · Estornada em {fmtData(item.dtEstorno ?? item.dtFechamento)}
        </p>
        {item.motivoEstorno && (
          <>
            <Separator className="my-1" />
            <p className="text-xs text-muted-foreground italic">{item.motivoEstorno}</p>
          </>
        )}
      </div>
      <div className="text-right">
        <span className="font-bold text-base">{fmt(item.totalLiquido)}</span>
      </div>
    </div>
  );
}

function NfceBadge({ notaEletronicaId }: { notaEletronicaId?: number }) {
  if (notaEletronicaId)
    return <Badge variant="secondary" className="text-xs">Com NFC-e</Badge>;
  return <Badge variant="outline" className="text-xs text-muted-foreground">Sem NFC-e</Badge>;
}
